import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChefHat,
  Calculator,
  BookOpen,
  AlertTriangle,
  List,
  DollarSign,
  TrendingUp,
} from 'lucide-react';

/* --- FAQs ---------------------------------------------------- */

const faqs = [
  {
    q: 'Como se calcula el precio de venta de un plato?',
    a: 'La formula basica es: PVP = Coste del plato / (Food cost objetivo / 100). Si tu plato cuesta 4 euros en ingredientes y quieres un food cost del 30%, el PVP minimo es 4 / 0,30 = 13,33 euros. A partir de ahi, ajustas segun posicionamiento, competencia y valor percibido por el cliente.',
  },
  {
    q: 'Que food cost objetivo debo usar para fijar precios?',
    a: 'Depende del tipo de establecimiento. Restaurantes casuales: 28-32%. Fine dining: 30-35% (compensado con ticket alto). Fast food: 25-28%. Catering: 22-28%. Cafeterias: 25-30%. Lo importante es que sea coherente con tu modelo de negocio y cubra todos los costes operativos.',
  },
  {
    q: 'Debo incluir el IVA en el precio de venta?',
    a: 'En Espana, los precios de carta siempre se muestran con IVA incluido (10% para hosteleria). Tu calculo de rentabilidad debe hacerse sobre el PVP sin IVA, porque el IVA no es un ingreso real del restaurante sino un impuesto que trasladas a Hacienda. Nuestra calculadora te muestra ambos valores.',
  },
  {
    q: 'Que es el multiplicador de precios?',
    a: 'El multiplicador es el factor por el que multiplicas el coste del plato para obtener el PVP. Con un food cost del 30%, el multiplicador es 3,33x (1/0,30). Con un 25%, es 4x. Es una forma rapida de calcular precios mentalmente: "este plato me cuesta 5 euros, x3,3 = 16,50 euros de PVP".',
  },
  {
    q: 'Puedo poner precios solo basandome en el food cost?',
    a: 'No exclusivamente. El food cost marca el precio minimo viable, pero tambien debes considerar: precio de la competencia, valor percibido por el cliente, posicionamiento del restaurante, elasticidad de la demanda y psicologia de precios (terminaciones en 0,50 o 0,90). El food cost es el suelo, no el techo.',
  },
  {
    q: 'Esta calculadora es gratuita?',
    a: 'Si, esta calculadora de precio de venta es 100% gratuita y no requiere registro. Para calcular automaticamente el PVP de toda tu carta basandote en escandallos reales con precios actualizados por OCR, puedes probar PLEKO gratis durante 14 dias.',
  },
];

/* --- JSON-LD -------------------------------------------------- */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const webAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculadora de Precio de Venta para Restaurantes',
  url: 'https://pleko.app/calculadora-precio-venta',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'All',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  author: { '@type': 'Organization', name: 'PLEKO' },
  description:
    'Calculadora gratuita de precio de venta (PVP) para restaurantes. Calcula el precio optimo de tus platos a partir del coste y el food cost objetivo.',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Calcular precio plato', item: 'https://pleko.app/calcular-precio-plato' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Calculadora de Precio de Venta',
      item: 'https://pleko.app/calculadora-precio-venta',
    },
  ],
};

/* --- Helpers -------------------------------------------------- */

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals).replace('.', ',');
}

/* --- Table of Contents ---------------------------------------- */

const toc = [
  { id: 'calculadora', label: 'Calculadora de precio de venta' },
  { id: 'como-usar', label: 'Como usar esta calculadora' },
  { id: 'formula', label: 'La formula del precio de venta' },
  { id: 'tabla-referencia', label: 'Tabla de referencia rapida' },
  { id: 'food-cost-tipo', label: 'Food cost objetivo segun tipo de establecimiento' },
  { id: 'metodos', label: '3 metodos para fijar precios' },
  { id: 'errores', label: 'Errores al fijar precios' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* --- Component ------------------------------------------------ */

export default function CalculadoraPrecioVentaPage() {
  const [costePlato, setCostePlato] = useState('');
  const [foodCostObj, setFoodCostObj] = useState(30);
  const [iva, setIva] = useState('10');

  /* -- Calculations -- */
  const coste = parseFloat(costePlato.replace(',', '.'));
  const ivaPct = parseFloat(iva.replace(',', '.')) || 0;
  const hasCoste = !isNaN(coste) && coste > 0;

  const pvpSinIva = hasCoste ? coste / (foodCostObj / 100) : 0;
  const pvpConIva = pvpSinIva * (1 + ivaPct / 100);
  const margenBruto = pvpSinIva - coste;
  const multiplicador = hasCoste ? pvpSinIva / coste : 0;

  const getTrafficLight = (pct: number) => {
    if (pct < 30) return { label: 'Excelente', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' };
    if (pct <= 35) return { label: 'Aceptable', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' };
    return { label: 'Revisar', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' };
  };

  const tl = getTrafficLight(foodCostObj);

  return (
    <>
      <Helmet>
        <title>Calculadora de Precio de Venta (PVP) para Restaurantes | Gratis | PLEKO</title>
        <meta
          name="description"
          content="Calculadora gratuita de precio de venta para restaurantes. Calcula el PVP optimo de tus platos a partir del coste, food cost objetivo e IVA. Sin registro."
        />
        <meta
          name="keywords"
          content="calculadora precio venta plato, calculadora PVP restaurante, calcular PVP plato, precio venta plato restaurante, calcular precio carta, calculadora precio menu"
        />
        <link rel="canonical" href="https://pleko.app/calculadora-precio-venta" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pleko.app/calculadora-precio-venta" />
        <meta
          property="og:title"
          content="Calculadora de Precio de Venta (PVP) para Restaurantes | Gratis | PLEKO"
        />
        <meta
          property="og:description"
          content="Calcula el precio de venta optimo de tus platos gratis: coste, food cost objetivo, IVA, margen bruto y multiplicador."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Calculadora de Precio de Venta (PVP) para Restaurantes | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Calcula el precio de venta optimo de tus platos gratis: coste, food cost objetivo, IVA, margen bruto y multiplicador."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-green-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Calculadora online gratuita — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Calculadora de <span className="text-green-600">Precio de Venta</span> para Restaurantes — Calcula el PVP Optimo de tus Platos
              </h1>
              <p className="text-xl text-slate-600 mb-4 max-w-2xl mx-auto">
                Introduce el coste de tu plato y el food cost objetivo. Obtendras al instante el PVP sugerido, el margen bruto, el multiplicador y el precio con IVA incluido.
              </p>
              <p className="text-base text-slate-500 max-w-xl mx-auto">
                Usa el semaforo visual para saber si tu food cost objetivo es saludable para tu negocio.
              </p>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta pagina</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-green-600 hover:text-green-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </section>

        {/* ========== CALCULATOR ========== */}
        <section id="calculadora" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20">
          <div className="border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              Calcula el precio de venta de tu plato
            </h2>

            {/* Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              {/* Coste del plato */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Coste del plato</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="4,50"
                    value={costePlato}
                    onChange={(e) => setCostePlato(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-lg font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">€</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">Coste total de ingredientes por racion</p>
              </div>

              {/* Food cost objetivo (slider) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Food cost objetivo: <span className={`font-bold ${tl.text}`}>{foodCostObj}%</span>
                </label>
                <input
                  type="range"
                  min={20}
                  max={40}
                  step={1}
                  value={foodCostObj}
                  onChange={(e) => setFoodCostObj(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-green-600 mt-3"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>20%</span>
                  <span>30%</span>
                  <span>40%</span>
                </div>
              </div>

              {/* IVA */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">IVA (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="10"
                    value={iva}
                    onChange={(e) => setIva(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-lg font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">%</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">10% para hosteleria en Espana</p>
              </div>
            </div>

            {/* Traffic light indicator */}
            <div className={`${tl.bg} border ${tl.border} rounded-xl p-4 mb-6 flex items-center gap-3`}>
              <span className={`w-4 h-4 rounded-full ${tl.dot} flex-shrink-0`} />
              <div>
                <span className={`font-semibold ${tl.text}`}>{tl.label}</span>
                <span className="text-slate-600 text-sm ml-2">
                  {foodCostObj < 30
                    ? '— Margen alto. Ideal para cubrir costes operativos elevados.'
                    : foodCostObj <= 35
                      ? '— Margen estandar en hosteleria. Viable si controlas costes fijos.'
                      : '— Margen ajustado. Cuidado con los costes operativos.'}
                </span>
              </div>
            </div>

            {/* Results */}
            {hasCoste && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Resultado
                </h3>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">PVP sin IVA</p>
                    <p className="text-2xl font-bold text-green-600">{fmt(pvpSinIva)} €</p>
                    <p className="text-xs text-slate-400 mt-1">Precio base</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">PVP con IVA</p>
                    <p className="text-2xl font-bold text-emerald-600">{fmt(pvpConIva)} €</p>
                    <p className="text-xs text-slate-400 mt-1">Precio en carta ({iva}% IVA)</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Margen bruto</p>
                    <p className="text-2xl font-bold text-blue-600">{fmt(margenBruto)} €</p>
                    <p className="text-xs text-slate-400 mt-1">Por plato vendido</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Multiplicador</p>
                    <p className="text-2xl font-bold text-slate-800">{fmt(multiplicador, 1)}x</p>
                    <p className="text-xs text-slate-400 mt-1">Coste &times; {fmt(multiplicador, 1)}</p>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4">
                  <p className="text-sm text-slate-700">
                    <strong>Resumen:</strong> Un plato con coste de <strong>{fmt(coste)} €</strong> y food cost objetivo del <strong>{foodCostObj}%</strong> debe venderse a un minimo de <strong>{fmt(pvpSinIva)} € sin IVA</strong> ({fmt(pvpConIva)} € en carta con {iva}% IVA). Tu beneficio bruto por plato es de <strong>{fmt(margenBruto)} €</strong>.
                  </p>
                </div>

                {foodCostObj > 35 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Food cost por encima del 35%</p>
                      <p className="text-sm text-red-700 mt-1">
                        Con un food cost del {foodCostObj}%, el margen bruto puede no ser suficiente para cubrir costes de personal, alquiler y otros gastos. Considera bajar al 30% (PVP: <strong>{fmt(coste / 0.3)} €</strong>).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA after calculator */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-12 print:hidden">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="font-bold text-slate-900 mb-1">
                  Calcula el precio de toda tu carta automaticamente
                </p>
                <p className="text-sm text-slate-600">
                  Con PLEKO, cada receta tiene su escandallo con coste real y PVP sugerido. Cuando cambian los precios de compra, los PVP se recalculan solos.
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-lg shadow-green-200 flex-shrink-0"
              >
                Probar PLEKO gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ========== SEO CONTENT ========== */}
          <div className="prose prose-lg max-w-none">

            {/* 1. Como usar */}
            <h2 id="como-usar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como usar esta calculadora de precio de venta
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Esta herramienta gratuita te permite calcular el precio de venta optimo de un plato en tres pasos:
            </p>
            <div className="space-y-4 mb-8">
              {[
                {
                  step: '1',
                  title: 'Introduce el coste del plato',
                  desc: 'Escribe el coste total de ingredientes por racion. Si no lo conoces, usa nuestra calculadora de escandallo para obtenerlo con precision, incluyendo mermas.',
                },
                {
                  step: '2',
                  title: 'Ajusta el food cost objetivo',
                  desc: 'Mueve el slider entre 20% y 40%. El valor recomendado para la mayoria de restaurantes es 28-32%. Cuanto menor sea el food cost, mayor sera tu margen bruto pero mas alto el precio de venta.',
                },
                {
                  step: '3',
                  title: 'Revisa el PVP con y sin IVA',
                  desc: 'La calculadora te muestra el precio sin IVA (para tu analisis de rentabilidad) y con IVA (el que pondras en la carta). Tambien ves el margen bruto y el multiplicador para calculos rapidos.',
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white font-bold flex items-center justify-center text-sm">
                    {step}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800">{title}</p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 2. La formula */}
            <h2 id="formula" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              La formula del precio de venta en hosteleria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La formula fundamental para calcular el precio de venta (PVP) de cualquier plato es:
            </p>

            <div className="bg-green-50 border border-green-100 rounded-xl p-6 mb-6">
              <p className="text-green-800 font-semibold mb-2">Formula principal</p>
              <p className="text-center text-xl font-bold text-green-600 font-mono">
                PVP = Coste del plato / (Food Cost objetivo / 100)
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> Un plato con coste de 4,50 € y food cost objetivo del 30% &rarr; PVP = 4,50 / 0,30 = <strong>15 €</strong> (sin IVA).
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
              <p className="text-blue-800 font-semibold mb-2">Con IVA incluido</p>
              <p className="text-center text-xl font-bold text-blue-600 font-mono">
                PVP carta = PVP sin IVA &times; (1 + IVA% / 100)
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> PVP sin IVA de 15 € con IVA del 10% &rarr; PVP carta = 15 &times; 1,10 = <strong>16,50 €</strong>.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-8">
              <p className="text-amber-800 font-semibold mb-2">Multiplicador (atajo mental)</p>
              <p className="text-center text-xl font-bold text-amber-600 font-mono">
                Multiplicador = 1 / (Food Cost objetivo / 100)
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> Food cost 30% &rarr; Multiplicador = 1 / 0,30 = <strong>3,33x</strong>. Plato de 4,50 € &times; 3,33 = 15 €. Es la forma mas rapida de calcular precios mentalmente.
              </p>
            </div>

            {/* 3. Tabla de referencia */}
            <h2 id="tabla-referencia" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla de referencia rapida: coste &rarr; PVP
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Consulta esta tabla para ver el PVP sugerido segun el coste de tu plato y el food cost objetivo. Todos los precios son sin IVA.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Coste del plato</th>
                    <th className="p-3 text-center font-medium">PVP al 25%</th>
                    <th className="p-3 text-center font-medium">PVP al 30%</th>
                    <th className="p-3 text-center font-medium">PVP al 35%</th>
                  </tr>
                </thead>
                <tbody>
                  {[2, 3, 4, 5, 6, 7, 8, 10, 12, 15].map((coste, i) => (
                    <tr key={coste} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-semibold text-slate-800">{fmt(coste)} €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-green-600 font-medium">{fmt(coste / 0.25)} €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-blue-600 font-bold">{fmt(coste / 0.30)} €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-amber-600 font-medium">{fmt(coste / 0.35)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-600 text-sm mb-8">
              <strong>Nota:</strong> Estos son PVP sin IVA. Para el precio en carta, suma el 10% de IVA (hosteleria Espana). Ejemplo: PVP 15 € &rarr; Carta: 16,50 €.
            </p>

            {/* 4. Food cost por tipo de establecimiento */}
            <h2 id="food-cost-tipo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que food cost objetivo elegir segun tu tipo de establecimiento
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No existe un food cost universal. El porcentaje optimo depende de tu modelo de negocio, tus costes fijos y tu ticket medio. Aqui tienes una guia orientativa:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de establecimiento</th>
                    <th className="p-3 text-center font-medium">Food cost recomendado</th>
                    <th className="p-3 text-center font-medium">Multiplicador</th>
                    <th className="p-3 text-left font-medium">Razon</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Fast food / take-away', '22-28%', '3,6-4,5x', 'Volumen alto, ticket bajo, costes fijos reducidos'],
                    ['Cafeteria / bakery', '25-30%', '3,3-4x', 'Productos de alto margen (cafe, bolleria)'],
                    ['Casual dining', '28-32%', '3,1-3,6x', 'Equilibrio entre volumen y ticket medio'],
                    ['Restaurante de gama media', '30-33%', '3-3,3x', 'Estandar de la industria en Espana'],
                    ['Fine dining', '30-35%', '2,9-3,3x', 'Ticket alto compensa food cost elevado'],
                    ['Hotel / buffet', '30-35%', '2,9-3,3x', 'Volumen alto, mermas elevadas por excedentes'],
                    ['Catering / eventos', '22-28%', '3,6-4,5x', 'Economia de escala en produccion masiva'],
                    ['Dark kitchen / delivery', '25-30%', '3,3-4x', 'Sin costes de sala, pero comisiones plataformas'],
                  ].map(([tipo, fc, mult, razon], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-green-600">{fc}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{mult}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{razon}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 5. Metodos para fijar precios */}
            <h2 id="metodos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              3 metodos para fijar el precio de venta de un plato
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-green-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Metodo del food cost (basado en coste)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es el metodo que usa esta calculadora. Partes del coste real del plato (escandallo) y aplicas un porcentaje objetivo. <strong>PVP = Coste / Food Cost %</strong>. Es el metodo mas fiable porque garantiza un margen minimo conocido. Imprescindible como punto de partida, aunque no deberia ser el unico criterio.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Metodo del multiplicador (factor fijo)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es una simplificacion del anterior. Defines un factor (ej: 3,5x) y lo aplicas a todos los platos: <strong>PVP = Coste &times; 3,5</strong>. Es rapido y facil de comunicar al equipo, pero no tiene en cuenta que distintos platos pueden tener costes operativos diferentes. Funciona bien como regla general para cartas homogeneas.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Metodo competitivo (basado en mercado)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Analizas los precios de restaurantes similares en tu zona y posicionas tu precio en consecuencia. Es util como referencia, pero <strong>peligroso si no conoces tu coste real</strong>: puedes acabar copiando los precios de un competidor que esta perdiendo dinero. Lo ideal es combinar este metodo con el del food cost: primero calculas tu PVP minimo por coste, luego ajustas segun el mercado.
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              El <strong>enfoque recomendado</strong> es usar el metodo del food cost como base, verificar con el multiplicador y ajustar con la referencia competitiva. Asi te aseguras un margen minimo, un precio coherente con tu carta y competitivo en tu mercado. Con PLEKO, puedes hacer esto automaticamente para toda tu carta.
            </p>

            {/* 6. Errores al fijar precios */}
            <h2 id="errores" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              6 errores habituales al fijar precios en un restaurante
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Fijar precios sin escandallo</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si no conoces el coste real de cada plato, estas fijando precios a ciegas. Un plato que "parece" barato de producir puede tener ingredientes con mermas altas que encarecen el coste real un 30-40% respecto a lo que imaginas.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Copiar precios de la competencia</h3>
                <p className="text-slate-700 leading-relaxed">
                  Tu competidor puede tener costes de alquiler, personal y proveedores completamente diferentes. Copiar sus precios sin conocer tu propio coste es una receta para perder dinero. Usa los precios de mercado como referencia, no como guia.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. No actualizar precios cuando suben los costes</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si el aceite sube un 20% y no tocas tus precios de carta, tu food cost real sube automaticamente. Muchos restaurantes tardan meses en reaccionar, erosionando su margen durante semanas. Con PLEKO, recibes alertas automaticas cuando un cambio de precio afecta a tu food cost.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Aplicar el mismo margen a todos los platos</h3>
                <p className="text-slate-700 leading-relaxed">
                  Un multiplicador fijo de 3x funciona bien como media, pero no todos los platos deben tener el mismo food cost. Los entrantes y postres suelen tener food costs mas bajos (20-25%) mientras que los principales pueden llegar al 35%. La <Link to="/ingenieria-de-menu" className="text-green-600 hover:underline">ingenieria de menu</Link> te ayuda a optimizar la rentabilidad global.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. Ignorar la psicologia de precios</h3>
                <p className="text-slate-700 leading-relaxed">
                  Un plato a 14,90 € se percibe como significativamente mas barato que uno a 15 €. Las terminaciones en ,50 o ,90 funcionan mejor que los numeros redondos. Tambien importa la posicion en la carta y la "ancla" de precio: colocar un plato caro primero hace que los demas parezcan mas asequibles.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">6. Olvidar el IVA en el calculo de rentabilidad</h3>
                <p className="text-slate-700 leading-relaxed">
                  Si tu plato esta a 16,50 € en carta (con IVA), tu ingreso real es 15 € (sin IVA). Calcular el food cost sobre los 16,50 € te dara un porcentaje artificialmente bajo que esconde la realidad de tu margen. Siempre calcula sobre PVP sin IVA.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre precios de venta</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/calcular-precio-plato', label: 'Guia completa: calcular precio de un plato' },
                { to: '/calculadora-food-cost', label: 'Calculadora de Food Cost gratuita' },
                { to: '/calculadora-escandallo', label: 'Calculadora de Escandallo online gratis' },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/margen-beneficio-restaurante', label: 'Margen de beneficio en restaurantes' },
                { to: '/ingenieria-de-menu', label: 'Ingenieria de menu: optimiza tu carta' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-green-600 hover:text-green-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-center text-white print:hidden">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula el precio de toda tu carta automaticamente</h2>
            <p className="text-green-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula el PVP optimo de cada plato basandose en escandallos reales, actualiza precios automaticamente con OCR de facturas y te avisa cuando un cambio de coste afecta a tu rentabilidad. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-green-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>

        {/* Sticky bottom CTA bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 py-3 px-4 z-50 print:hidden">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600 hidden sm:block">
              Calcula el precio optimo de toda tu carta. <strong>Prueba PLEKO gratis 14 dias</strong>
            </p>
            <p className="text-sm text-slate-600 sm:hidden">
              <strong>Prueba PLEKO gratis</strong>
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-sm flex-shrink-0"
            >
              Probar gratis
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
